import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Trash2, Edit, X, Check, Search } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

interface TruckCleaning {
  id: string;
  truck_number: string;
  cleaning_date: string;
  service_type: 'Cleaning' | 'Mattress';
  firma: string | null;
  paid_ariana: boolean;
  invoice: string | null;
  date_paid: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

export function TruckCleaningView() {
  const [cleaningRecords, setCleaningRecords] = useState<TruckCleaning[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<TruckCleaning | null>(null);
  const { showToast } = useToast();

  // Form state
  const [truckNumber, setTruckNumber] = useState('');
  const [truckSearchQuery, setTruckSearchQuery] = useState('');
  const [trucks, setTrucks] = useState<any[]>([]);
  const [showTruckDropdown, setShowTruckDropdown] = useState(false);
  const [cleaningDate, setCleaningDate] = useState(new Date().toISOString().split('T')[0]);
  const [serviceType, setServiceType] = useState<'Cleaning' | 'Mattress'>('Cleaning');
  const [firma, setFirma] = useState('');
  const [paidAriana, setPaidAriana] = useState(false);
  const [invoice, setInvoice] = useState('');
  const [datePaid, setDatePaid] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCleaningRecords();
    loadTrucks();
  }, []);

  const loadCleaningRecords = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('truck_cleaning')
        .select('*')
        .order('cleaning_date', { ascending: false });

      if (error) throw error;
      setCleaningRecords(data || []);
    } catch (error: any) {
      showToast('error', error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTrucks = async () => {
    try {
      const { data, error } = await supabase
        .from('trucks')
        .select('id, unit_number, make, model')
        .order('unit_number');

      if (error) throw error;
      setTrucks(data || []);
    } catch (error) {
      console.error('Error loading trucks:', error);
    }
  };

  const filteredTrucks = trucks.filter(truck =>
    truck.unit_number.toLowerCase().includes(truckSearchQuery.toLowerCase())
  );

  const handleTruckSelect = (truck: any) => {
    setTruckNumber(truck.unit_number);
    setTruckSearchQuery(truck.unit_number);
    setShowTruckDropdown(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this cleaning record?')) return;

    try {
      const { error } = await supabase
        .from('truck_cleaning')
        .delete()
        .eq('id', id);

      if (error) throw error;
      showToast('success', 'Record deleted');
      loadCleaningRecords();
    } catch (error: any) {
      showToast('error', error.message);
    }
  };

  const handleTogglePaid = async (record: TruckCleaning) => {
    try {
      const newPaidStatus = !record.paid_ariana;
      const updateData: any = {
        paid_ariana: newPaidStatus,
        updated_at: new Date().toISOString(),
      };

      // If marking as paid, set date_paid to today
      // If marking as unpaid, clear date_paid
      if (newPaidStatus) {
        updateData.date_paid = new Date().toISOString().split('T')[0];
      } else {
        updateData.date_paid = null;
      }

      const { error } = await supabase
        .from('truck_cleaning')
        .update(updateData)
        .eq('id', record.id);

      if (error) throw error;
      showToast('success', newPaidStatus ? 'Marked as paid' : 'Marked as unpaid');
      loadCleaningRecords();
    } catch (error: any) {
      showToast('error', error.message);
    }
  };

  const openModal = (record?: TruckCleaning) => {
    if (record) {
      setEditingRecord(record);
      setTruckNumber(record.truck_number);
      setTruckSearchQuery(record.truck_number);
      setCleaningDate(record.cleaning_date);
      setServiceType(record.service_type);
      setFirma(record.firma || '');
      setPaidAriana(record.paid_ariana);
      setInvoice(record.invoice || '');
      setDatePaid(record.date_paid || '');
    } else {
      setEditingRecord(null);
      setTruckNumber('');
      setTruckSearchQuery('');
      setCleaningDate(new Date().toISOString().split('T')[0]);
      setServiceType('Cleaning');
      setFirma('');
      setPaidAriana(false);
      setInvoice('');
      setDatePaid('');
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRecord(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!truckNumber.trim()) {
      showToast('error', 'Truck number is required');
      return;
    }

    try {
      setSaving(true);

      const dataToSave = {
        truck_number: truckNumber,
        cleaning_date: cleaningDate,
        service_type: serviceType,
        firma: firma || null,
        paid_ariana: paidAriana,
        invoice: invoice || null,
        date_paid: datePaid || null,
        updated_at: new Date().toISOString(),
      };

      if (editingRecord) {
        const { error } = await supabase
          .from('truck_cleaning')
          .update(dataToSave)
          .eq('id', editingRecord.id);

        if (error) throw error;
        showToast('success', 'Cleaning record updated successfully');
      } else {
        const { error } = await supabase
          .from('truck_cleaning')
          .insert({
            ...dataToSave,
            created_at: new Date().toISOString(),
          });

        if (error) throw error;
        showToast('success', 'Cleaning record added successfully');
      }

      loadCleaningRecords();
      closeModal();
    } catch (error: any) {
      showToast('error', error.message || 'Failed to save cleaning record');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Truck Cleaning</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">Track truck washing and mattress cleaning services</p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center justify-center gap-2 px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium w-full sm:w-auto"
        >
          <Plus className="w-5 h-5" />
          <span>Add Cleaning Record</span>
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Truck #</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Service Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Firma</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Paid-Ariana</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Invoice</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Date Paid</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {cleaningRecords.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    No cleaning records found. Click "Add Cleaning Record" to create one.
                  </td>
                </tr>
              ) : (
                cleaningRecords.map((record) => (
                  <tr key={record.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-medium text-slate-900">{record.truck_number}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {new Date(record.cleaning_date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                        record.service_type === 'Cleaning' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {record.service_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{record.firma || '-'}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleTogglePaid(record)}
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg font-medium transition-colors cursor-pointer ${
                          record.paid_ariana
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                        title="Click to toggle payment status"
                      >
                        <Check className="w-4 h-4" />
                        {record.paid_ariana ? 'Paid' : 'Not Paid'}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{record.invoice || '-'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {record.date_paid ? new Date(record.date_paid).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openModal(record)}
                          className="p-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Edit record"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(record.id)}
                          className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete record"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">
                {editingRecord ? 'Edit Cleaning Record' : 'Add Cleaning Record'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-slate-600" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Truck Number *
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={truckSearchQuery}
                    onChange={(e) => {
                      setTruckSearchQuery(e.target.value);
                      setShowTruckDropdown(true);
                    }}
                    onFocus={() => setShowTruckDropdown(true)}
                    placeholder="Search truck number..."
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  
                  {showTruckDropdown && filteredTrucks.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredTrucks.map((truck) => (
                        <button
                          key={truck.id}
                          type="button"
                          onClick={() => handleTruckSelect(truck)}
                          className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-slate-100 last:border-b-0"
                        >
                          <div className="font-medium text-slate-900">{truck.unit_number}</div>
                          <div className="text-sm text-slate-600">{truck.make} {truck.model}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Cleaning Date *
                </label>
                <input
                  type="date"
                  value={cleaningDate}
                  onChange={(e) => setCleaningDate(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  Service Type *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setServiceType('Cleaning')}
                    className={`py-3 rounded-lg font-semibold transition-all ${
                      serviceType === 'Cleaning'
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    Cleaning
                  </button>
                  <button
                    type="button"
                    onClick={() => setServiceType('Mattress')}
                    className={`py-3 rounded-lg font-semibold transition-all ${
                      serviceType === 'Mattress'
                        ? 'bg-purple-600 text-white shadow-lg'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    Mattress
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Firma
                </label>
                <input
                  type="text"
                  value={firma}
                  onChange={(e) => setFirma(e.target.value)}
                  placeholder="Enter company name..."
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paidAriana}
                    onChange={(e) => setPaidAriana(e.target.checked)}
                    className="w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-slate-700">Paid-Ariana</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Invoice
                </label>
                <input
                  type="text"
                  value={invoice}
                  onChange={(e) => setInvoice(e.target.value)}
                  placeholder="Enter invoice number..."
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Date Paid
                </label>
                <input
                  type="date"
                  value={datePaid}
                  onChange={(e) => setDatePaid(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Footer */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-3 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : editingRecord ? 'Update' : 'Add Record'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
